import { Injectable } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { DigitalMemoryLog } from "../models/digital-memory-log.interface";
import { MailPopupComponent } from "../components/mail-popup/mail-popup.component";
import { RenamePopupComponent } from "../components/rename-popup/rename-popup.component";
import { DIGITAL_MEMORY_LOG_CONSTANTS } from "../constants/digital-memory-log.constants";
import { toUrlFriendlySlug } from "src/app/shared/utils/url-slug.util";

@Injectable({
  providedIn: "root",
})
export class DigitalMemoryLogActionsService {
  constructor(
    private modalService: NgbModal,
    private router: Router,
    private toastr: ToastrService
  ) {}

  /**
   * Open mail popup for single digital memory log
   */
  sendMailById(entity: DigitalMemoryLog, onSuccess?: () => void): void {
    const modalRef = this.modalService.open(MailPopupComponent, {
      centered: true,
      size: "lg",
      backdrop: true,
      keyboard: true,
    });

    modalRef.componentInstance.model = entity || {};
    modalRef.componentInstance.digitalLodId = entity.id || {};
    modalRef.componentInstance.type = "single";

    modalRef.result.then(
      result => {
        if (onSuccess) {
          onSuccess();
        }
      },
      reason => {
        // Handle rejection if needed
      }
    );
  }

  /**
   * Open mail popup for multiple digital memory logs
   */
  multiSendMailById(entity: DigitalMemoryLog, onSuccess?: () => void): void {
    const modalRef = this.modalService.open(MailPopupComponent, {
      centered: true,
      size: "lg",
      backdrop: true,
      keyboard: true,
    });

    modalRef.componentInstance.model = entity || {};
    modalRef.componentInstance.digitalLodId = entity.id || {};
    modalRef.componentInstance.type = "multi";

    modalRef.result.then(
      result => {
        if (onSuccess) {
          onSuccess();
        }
      },
      reason => {
        // Handle rejection if needed
      }
    );
  }

  /**
   * Copy preview link to clipboard
   */
  copyLink(entity: DigitalMemoryLog): void {
    const urlFriendlyName = toUrlFriendlySlug(entity.name);
    const previewLink = `${window.location.origin}/preview-${urlFriendlyName}-${entity.id}`;

    this.copyToClipboard(previewLink);
  }

  /**
   * Open rename popup
   */
  renameDigitalLog(entity: DigitalMemoryLog, onSuccess?: () => void): void {
    const modalRef = this.modalService.open(RenamePopupComponent, {
      centered: true,
      size: "lg",
      backdrop: true,
      keyboard: true,
    });

    modalRef.componentInstance.model = entity || {};
    modalRef.componentInstance.digitalLogId = entity.id || {};
    modalRef.componentInstance.currentName = entity.name || "";

    modalRef.result.then(
      result => {
        if (onSuccess) {
          onSuccess();
        }
      },
      reason => {
        // Handle rejection if needed
      }
    );
  }

  /**
   * Navigate to edit page
   */
  editDigitalLog(entity: DigitalMemoryLog, shipId: string, shipName: string): void {
    if (entity && entity.id) {
      const urlFriendlyShipName = toUrlFriendlySlug(shipName, { emptyFallback: "ship" });
      this.router.navigate([`/overview/ship-${urlFriendlyShipName}-${shipId}/edit-digital-memory-log-${entity.id}`]);
    }
  }

  /**
   * Navigate to preview page
   */
  previewDigitalLog(entity: DigitalMemoryLog, route: any): void {
    const urlFriendlyName = toUrlFriendlySlug(entity.name);
    const queryParams = {
      ...route.snapshot.queryParams,
      type: "list",
    };

    this.router.navigate([`preview-${urlFriendlyName}-${entity.id}`], {
      relativeTo: route,
      queryParams: queryParams,
    });
  }

  /**
   * Copy text to clipboard with fallback
   */
  private copyToClipboard(text: string): void {
    (navigator as any).clipboard
      .writeText(text)
      .then(() => {
        this.toastr.success("Preview link copied to clipboard!", "Success");
      })
      .catch(err => {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            this.toastr.success("Preview link copied to clipboard!", "Success");
          } else {
            this.toastr.error("Failed to copy link", "Error");
          }
        } catch (error) {
          this.toastr.error("Failed to copy link", "Error");
        }

        document.body.removeChild(textArea);
      });
  }
}
